import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../../reducers';
import {
  uploadBackupRecordThunk,
  dismissBackupRecordThunk,
} from '../../../settings/settingsSlice';

interface BackupRecord {
  fileId: string;
  startedAt: number;
  teamId?: string;
  folderId?: string;
  uploadId?: string;
  status?: 'pending' | 'dismissed' | 'uploaded' | 'recovered'; // Optional for backwards compatibility
}

const getTimeAgo = (timestamp: number): string => {
  const now = Date.now();
  const diffMs = now - timestamp;
  const diffMinutes = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));

  if (diffMinutes < 1) {
    return 'Just now';
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  }
  return new Date(timestamp).toLocaleDateString();
};

export const useRecoveryLogic = () => {
  const dispatch = useDispatch();
  const { backupRecords, recoveryState } = useSelector(
    (state: RootState) => state.settings.recovery
  );

  // Check for recent recoverable files from the last 4 hours
  // TODO: After cleanup period (10 days), remove time-based logic and use only status-based filtering
  // This is because existing recordings without status will all show up if we switch now
  const recentRecoverableFiles = useMemo(() => {
    if (!backupRecords) {
      return [];
    }

    const fourHoursAgo = Date.now() - 4 * 60 * 60 * 1000;
    return backupRecords.filter((record) => {
      const isRecent = record.startedAt > fourHoursAgo;
      // Default to 'pending' if status is missing (for backwards compatibility)
      const isPending = (record.status || 'pending') === 'pending';
      return isRecent && isPending;
    });
  }, [backupRecords]);

  const handleRecover = (record: BackupRecord) => {
    dispatch(uploadBackupRecordThunk(record));
  };

  const handleDismiss = (fileId: string) => {
    dispatch(dismissBackupRecordThunk(fileId));
  };

  const firstRecord = recentRecoverableFiles[0];
  const isRecovering = firstRecord
    ? recoveryState[firstRecord.fileId]?.status === 'ongoing'
    : false;
  const progress = firstRecord
    ? recoveryState[firstRecord.fileId]?.progress || 0
    : 0;
  const hasMultiple = recentRecoverableFiles.length > 1;

  return {
    recentRecoverableFiles,
    handleRecover,
    handleDismiss,
    getTimeAgo,
    firstRecord,
    isRecovering,
    progress,
    hasMultiple,
    hasRecoverableFiles: recentRecoverableFiles.length > 0,
  };
};

export type { BackupRecord };
